<template>
  <div class="page-class page-escalation-matrix">
    <h1 class="page-title text-center">Escalation Matrix</h1>

    <div v-if="loading" class="text-center py-5">
      <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
      <p class="mt-2 mb-0">Loading…</p>
    </div>

    <b-card
      v-else-if="escalationMatrix"
      class="box-shadow2"
      :class="{ 'bg-dark text-light border-secondary': isDarkMode }"
    >
      <b-row class="align-items-center mb-3">
        <b-col cols="12" md="auto">
          <b-badge :variant="escalationMatrix.isActive ? 'success' : 'secondary'" class="mt-2 mt-md-0">
            {{ escalationMatrix.isActive ? 'Active' : 'Inactive' }}
          </b-badge>
        </b-col>
        <b-col cols="12" md="auto" class="ml-md-auto mt-2 mt-md-0 text-md-right">
          <!-- <b-button variant="success" class="box-shadow2 mr-2" :to="{ name: 'escalation-matrix-new' }">
            <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
            New matrix
          </b-button> -->
          <b-button
            variant="outline-success"
            class="box-shadow2 mr-2"
            :disabled="loading || !previewRows.length"
            @click="onDownloadCurrentMatrix"
          >
            <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
            Download current matrix
          </b-button>
          <b-button variant="outline-primary" class="box-shadow2 mr-2" :to="editRoute">
            <font-awesome-icon :icon="['fas', 'edit']" class="mr-1" />
            Edit
          </b-button>
          <b-button variant="outline-secondary" class="box-shadow2" :disabled="loading" @click="onReload">
            <font-awesome-icon :icon="['fas', 'sync']" class="mr-1" />
            Reload
          </b-button>
        </b-col>
      </b-row>

      <p v-if="escalationMatrix.updatedAt" class="small text-muted mb-3">
        Last updated: {{ escalationMatrix.updatedAt }}
      </p>

      <b-table
        v-if="previewRows.length"
        :items="previewRows"
        :fields="tableFields"
        responsive
        striped
        hover
        small
        :tbody-tr-class="isDarkMode ? 'table-dark' : ''"
        class="mb-0 escalation-matrix-table"
      >
        <template #cell(rowNum)="row">
          {{ row.index + 1 }}
        </template>
        <template #cell(L1)="data">
          <level-preview :level="data.item.L1" />
        </template>
        <template #cell(L2)="data">
          <level-preview :level="data.item.L2" />
        </template>
        <template #cell(L3)="data">
          <level-preview :level="data.item.L3" />
        </template>
      </b-table>

      <p v-else class="text-muted mb-0">No rows in this matrix.</p>
    </b-card>

    <b-alert v-else show variant="secondary" class="box-shadow2">
      Escalation matrix could not be loaded. Use Reload after fixing the issue, or check your session permissions.
    </b-alert>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { matrixToBulkCsv, downloadTextFile } from '@/helper/escalationMatrixForm';

const LevelPreview = {
  name: 'LevelPreview',
  functional: true,
  props: {
    level: { type: Object, default: null }
  },
  render(h, ctx) {
    const L = ctx.props.level;
    if (!L) {
      return h('span', { class: 'text-muted' }, '—');
    }
    const users = Array.isArray(L.users) ? L.users : [];
    const children = [];
    if (L.role) {
      children.push(h('div', { class: 'small text-muted mb-1' }, L.role));
    }
    if (!users.length) {
      children.push(h('div', { class: 'small text-muted' }, 'No users'));
      return h('div', { class: 'level-preview' }, children.length ? children : [h('span', { class: 'text-muted' }, '—')]);
    }
    users.forEach((u, i) => {
      const stores = Array.isArray(u.stores) && u.stores.length ? u.stores.join('; ') : '';
      const userLines = [
        h('div', [h('strong', u.name || '—')]),
        u.mobile ? h('div', { class: 'small' }, u.mobile) : null,
        h('div', { class: 'small' }, u.email || ''),
        stores ? h('div', { class: 'small text-muted' }, `Stores: ${stores}`) : null
      ].filter(Boolean);
      children.push(
        h('div', { key: `u-${i}`, class: 'user-preview mb-2 pb-1 border-bottom' }, userLines)
      );
    });
    return h('div', { class: 'level-preview' }, children);
  }
};

export default {
  name: 'EscalationMatrixList',
  components: { LevelPreview },
  metaInfo() {
    return {
      title: 'Escalation Matrix'
    };
  },
  data() {
    return {
      tableFields: [
        {
          key: 'rowNum',
          label: '#',
          thClass: 'text-center',
          tdClass: 'align-middle text-center',
          thStyle: { width: '44px' }
        },
        { key: 'department', label: 'Department', thStyle: { minWidth: '120px' } },
        { key: 'tat', label: 'TAT (days)', thStyle: { width: '96px' } },
        { key: 'L1', label: 'Level 1', thStyle: { minWidth: '160px' } },
        { key: 'L2', label: 'Level 2', thStyle: { minWidth: '160px' } },
        { key: 'L3', label: 'Level 3', thStyle: { minWidth: '160px' } }
      ]
    };
  },
  mounted() {
    this.load();
  },
  computed: {
    ...mapState('qms', ['escalationMatrix', 'loading']),
    ...mapState('darkMode', ['isDarkMode']),
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isQMSAdmin']),
    previewRows() {
      const doc = this.escalationMatrix;
      if (!doc || !Array.isArray(doc.matrix)) {
        return [];
      }
      return doc.matrix.map((row, index) => ({
        rowNum: index + 1,
        department: row.department,
        tat: row.tat,
        L1: row.L1,
        L2: row.L2,
        L3: row.L3
      }));
    },
    editRoute() {
      const key = (this.escalationMatrix && this.escalationMatrix.key) || 'hr_management';
      return {
        name: 'escalation-matrix-edit',
        params: { key }
      };
    }
  },
  methods: {
    ...mapActions('qms', ['listEscalationMatrix']),
    async load() {
      const response = await this.listEscalationMatrix({ router: this.$router, key: 'hr_management' });
      if (!response || !Array.isArray(response.matrix) || !this.user || !Array.isArray(this.user.roles)) {
        return;
      }
      const loggedInRoles = this.user.roles
        .map(role => String(role && role.name ? role.name : '').trim().toLowerCase())
        .filter(Boolean);
      if (!loggedInRoles.length) {
        return;
      }
      const matrixRoles = response.matrix
        .flatMap(row => ['L1', 'L2', 'L3'].map(level => row && row[level] && row[level].role))
        .map(role => String(role || '').trim().toLowerCase())
        .filter(Boolean);
      const hasMappedRole = matrixRoles.some(role => loggedInRoles.includes(role));
      if (hasMappedRole && !this.isQMSAdmin) {
        this.$router.push('/qms');
      }
    },
    onReload() {
      this.load();
    },
    onDownloadCurrentMatrix() {
      const doc = this.escalationMatrix;
      if (!doc || !Array.isArray(doc.matrix) || !doc.matrix.length) {
        return;
      }
      const key = (doc.key || 'hr_management').replace(/[^\w-]+/g, '_');
      downloadTextFile({
        content: matrixToBulkCsv(doc.matrix),
        filename: `escalation-matrix-${key}.csv`,
        mimeType: 'text/csv;charset=utf-8'
      });
    }
  }
};
</script>

<style scoped>
.escalation-matrix-table ::v-deep th {
  white-space: nowrap;
}
.level-preview {
  line-height: 1.35;
}
.user-preview:last-child {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>
